import {List, Space, Stack} from '@mantine/core';
import {PlainTextCodeBox} from "./PlainTextCodeBox";
import {CodeBox} from "./CodeBox";
import {NormalText} from "./NormalText";
import {Overview} from "./Overview";
import {PageDescriptionText} from "./PageDescriptionText";
import {PageTitleText} from "./PageTitleText";
import {CommandNameText} from "./CommandNameText";
import {CommandAbbreviationText} from "./CommandAbbreviationText";

const inputs = [
    {input: "SRR1513231"},
    {input: "SRR1513231,DRR259113"},
    {input: "list_of_accession_numbers.txt"},
    {input: "SRR1513231_10.counts"},
    {input: "my_config.toml"},
    {input: "SRR1513231 --kmer 6"},
    {input: "SRR1513231 --output-dir C:\\Users\\...\\Desktop\\img-output-folder"},
    {input: "SRR1513231 --size 100"},
    {input: "--help"}
]

const codeTexts = inputs.map((input) => {
    return {...input, command: "lagg", subCommand: "cgr"};
})

const list = `
SRR1513231
DRR259113
ERR4470013
`;

export function CGRTextSection() {
    return (
        <Stack
            justify="flex-start"
            align="flex-start"
            gap="xl"
            mr="xl"
            ml="xl"
            mb="xl"
            maw="60rem"
        >
            <Stack
                gap="xs"
                justify="flex-start"
            >

                <Overview/>

                <PageTitleText>
                    cgr
                </PageTitleText>

                <PageDescriptionText>
                    This command is the primary tool for generating images.
                </PageDescriptionText>

                <Space h="sm"/>

                <NormalText>
                    The cgr command is a versatile tool designed to generate images from genome data,
                    primarily using k-mer files or NCBI database accession numbers.
                    This tool allows users to input data files, specify k-mer sizes, adjust output paths, and scale the
                    resulting images.
                    The command is flexible, providing options for single or multiple inputs and customization of image
                    outputs.
                </NormalText>
            </Stack>
            <Stack gap={'m'} id='input'>
                <CommandNameText>
                    Input Argument
                </CommandNameText>

                <NormalText>
                    A required argument that can go before or after the option flags. <Space h="xs"/>
                    For this command, the input options must be one of the following:
                    <List>
                        <List.Item>A path to a text file containing k-mer counts.</List.Item>
                        <List.Item>An SRA run accession number.</List.Item>
                        <List.Item>A path to a text file containing line-separated SRA run accession
                            numbers.</List.Item>
                        <List.Item>A config file containing specialized options outlined in the “Config File” page.
                            <Space/>Note that for config input, only the ‘output-dir’ option is usable.</List.Item>
                    </List>
                    <Space h="xs"/>
                    To use multiple inputs, provide a comma-separated list of any of the above.
                </NormalText>
                <Space h="sm"/>
                <NormalText>
                    This example below uses a single run accession number to generate an image:
                </NormalText>
                <CodeBox codeText={codeTexts[0]}/>

                <NormalText>
                    To use multiple accession numbers, simply comma-separate them:
                </NormalText>
                <CodeBox codeText={codeTexts[1]}/>

                <NormalText>
                    You can also use a list to a text file of line-separated run accession numbers like so:
                </NormalText>
                <CodeBox codeText={codeTexts[2]}/>

                <NormalText>
                    list_of_accession_numbers.txt:
                </NormalText>
                <PlainTextCodeBox>{list}</PlainTextCodeBox>

                <NormalText>
                    You can also use a pre-generated counts file from jellyfish:
                </NormalText>
                <CodeBox codeText={codeTexts[3]}/>

                <NormalText>
                    Finally, if you’d like to use a config file, just point to a valid TOML file:
                </NormalText>
                <CodeBox codeText={codeTexts[4]}/>
            </Stack>

            <Stack gap={'m'} id="kmer">
                <CommandNameText>
                    --kmer <CommandAbbreviationText> (-k)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Specify the k-mer number for generating the image(s). Only used when inputting accession numbers as
                    the genome data.
                </NormalText>

                <NormalText>
                    Will generate an image corresponding to the genome data represented by accession number SRR1513231
                    in the NCBI database using a k-mer value of 6.
                </NormalText>
                <CodeBox codeText={codeTexts[5]}/>
            </Stack>

            <Stack gap={'m'} id="output-path">
                <CommandNameText>
                    --output-dir <CommandAbbreviationText> (-o)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Use this to specify an alternate save directory for your generated images.
                    If nothing is specified, the default location is where the terminal is located.
                </NormalText>

                <NormalText>
                    Will output the image generated by accession number SRR1513231 to a folder named img-output-folder
                    on the user's Desktop.
                </NormalText>
                <CodeBox codeText={codeTexts[6]}/>
            </Stack>

            <Stack gap={'m'} id="scale-size">
                <CommandNameText>
                    --size <CommandAbbreviationText> (-s)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Used to state the size of the generated image if wanting to change it from the default image size.
                    Specify the side pixel length of the image.
                </NormalText>

                <NormalText>
                    Will scale down the image generated by accession number SRR1513231 to an 100x100 pixel image.
                </NormalText>
                <CodeBox codeText={codeTexts[7]}/>
            </Stack>

            <Stack gap={'m'} id="help">
                <CommandNameText>
                    --help
                </CommandNameText>

                <NormalText>
                    Will show the list of command options for cgr in the terminal along with default values, input type,
                    and example use cases.
                </NormalText>
                <CodeBox codeText={codeTexts[8]}/>

                <Space h='xs'/>
            </Stack>
        </Stack>
    );
}